import { useEffect, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { HttpApiService } from "@/shared/services/httpApi.service";

import { WalletService } from "../services/walletsService";
import type { OrganizationWalletAggregates } from "../types/wallets-api.types";

// Mirrors `useOrganizationWalletBalance` singleton pattern.
// `invalidateOrganizationWalletAllocation` must be called after pocket
// transfers and card top-ups so the chip strip stays in sync.
const STALE_AFTER_MS = 30_000;

type CacheEntry = {
  aggregates: OrganizationWalletAggregates;
  fetchedAt: number;
};

type InFlight = Promise<OrganizationWalletAggregates>;

const entries = new Map<string, CacheEntry>();
const inflight = new Map<string, InFlight>();
const listeners = new Map<string, Set<() => void>>();

function notify(orgId: string): void {
  const set = listeners.get(orgId);
  if (!set) return;
  for (const fn of set) fn();
}

async function loadFromServer(orgId: string): Promise<OrganizationWalletAggregates> {
  const response = await WalletService.getOrganizationWalletAggregates(orgId);
  return response.data;
}

async function ensureFresh(orgId: string): Promise<OrganizationWalletAggregates> {
  const existing = entries.get(orgId);
  if (existing && Date.now() - existing.fetchedAt < STALE_AFTER_MS) {
    return existing.aggregates;
  }

  let promise = inflight.get(orgId);
  if (!promise) {
    promise = loadFromServer(orgId)
      .then((aggregates) => {
        entries.set(orgId, { aggregates, fetchedAt: Date.now() });
        return aggregates;
      })
      .finally(() => {
        inflight.delete(orgId);
      });
    inflight.set(orgId, promise);
  }

  const aggregates = await promise;
  notify(orgId);
  return aggregates;
}

export function invalidateOrganizationWalletAllocation(orgId: string): void {
  entries.delete(orgId);
  notify(orgId);
}

export function useOrganizationWalletAllocation(): {
  aggregates: OrganizationWalletAggregates | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
} {
  const { user } = useAuth();
  const orgId = user?.organization_id ?? user?.organization?.id ?? null;

  const [aggregates, setAggregates] = useState<OrganizationWalletAggregates | null>(() =>
    orgId ? (entries.get(orgId)?.aggregates ?? null) : null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orgId) {
      // Reset on logout / org switch so the previous org's aggregates do not
      // leak into the new session.
      /* eslint-disable react-hooks-eslint/set-state-in-effect */
      setAggregates(null);
      setIsLoading(false);
      /* eslint-enable react-hooks-eslint/set-state-in-effect */
      return;
    }

    let cancelled = false;

    const subscribe = () => {
      const current = entries.get(orgId)?.aggregates ?? null;
      if (!cancelled) setAggregates(current);
    };

    const set = listeners.get(orgId) ?? new Set();
    set.add(subscribe);
    listeners.set(orgId, set);

    setIsLoading(true);
    setError(null);
    ensureFresh(orgId)
      .then((next) => {
        if (cancelled) return;
        setAggregates(next);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(HttpApiService.getErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
      const next = listeners.get(orgId);
      if (next) {
        next.delete(subscribe);
        if (next.size === 0) listeners.delete(orgId);
      }
    };
  }, [orgId]);

  const refresh = async (): Promise<void> => {
    if (!orgId) return;
    invalidateOrganizationWalletAllocation(orgId);
    try {
      const fresh = await ensureFresh(orgId);
      setAggregates(fresh);
    } catch (err: unknown) {
      setError(HttpApiService.getErrorMessage(err));
    }
  };

  return { aggregates, isLoading, error, refresh };
}
